import { IAPIRoutes } from "../../../../config";

export type IUserRepository = ReturnType<typeof UserRepositoryFactory>;

export const UserRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, PUT, POST } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getUserBetaFeatures: IGetUserBetaFeaturesAPI = async () =>
    GET(
      APIRoutes.apiUserBetaFeature,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const updateUserInfo: IUpdateUserInfoAPI = async (payload) =>
    PUT(APIRoutes.apiUpdateUserInfo, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const signupUser: ISignupUserAPI = async (payload) =>
    POST(APIRoutes.apiSignup, payload);

  const getAdminPendingUsers: IGetAdminPendingUsersAPI = async (params) => {
    const query: Record<string, string | number> = {
      limit: params.limit,
      offset: params.offset,
    };
    if (params.email) query.email = params.email;
    if (params.phone) query.phone = params.phone;
    return GET(APIRoutes.apiAdminPendingUsers, query, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const activateAdminPendingUser: IActivateAdminPendingUserAPI = async ({
    cognito_user_id,
    body,
  }) =>
    POST(
      APIRoutes.apiAdminActivateUser.replace(
        "{cognito_user_id}",
        encodeURIComponent(cognito_user_id)
      ),
      body,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getClinicUsers: IGetClinicUsersAPI = async ({
    clinic_id,
    limit = 50,
    offset = 0,
  }) =>
    GET(
      APIRoutes.apiGetClinicUsers.replace(
        "{clinic_id}",
        encodeURIComponent(clinic_id)
      ),
      { limit, offset },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const setUserPassword: ISetUserPasswordAPI = async ({
    clinic_id,
    cognito_user_id,
    body,
  }) =>
    POST(
      APIRoutes.apiResendInvite
        .replace("{clinic_id}", encodeURIComponent(clinic_id))
        .replace(
          "{cognito_user_id}",
          encodeURIComponent(cognito_user_id),
        ),
      body,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
    );

  const disconnectDevice: IDisconnectDeviceAPI = async ({
    clinic_id,
    patient_id,
    device_id,
  }) =>
    POST(
      APIRoutes.apiDisconnectDevice
        .replace("{clinic_id}", encodeURIComponent(clinic_id))
        .replace("{patient_id}", encodeURIComponent(patient_id))
        .replace("{device_id}", encodeURIComponent(device_id)),
      // Endpoint takes no body, but the API provider's POST signature
      // expects one — empty object is the conventional no-op payload.
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      },
    );

  return {
    getUserBetaFeatures,
    updateUserInfo,
    signupUser,
    getAdminPendingUsers,
    activateAdminPendingUser,
    getClinicUsers,
    setUserPassword,
    disconnectDevice,
  };
};
